import * as Sentry from "@sentry/react";

import { ProxyLocationTag } from "common/components/Proxy";
import Skeleton from "common/components/ui/Skeleton";
import useProxies from "common/datahooks/useProxies";
import useWorkspaceProxies from "common/datahooks/useWorkspaceProxies";
import type { WorkspaceAccount } from "common/types";

import useAccountProxy from "../../datahooks/useAccountProxy";

export default function AccountProxyTag({
  account,
}: {
  account: WorkspaceAccount;
}) {
  const { accountProxy, isLoadingAccountProxy } = useAccountProxy(account);
  const { proxies, isLoadingProxies } = useProxies();
  const { workspaceProxies, isLoadingWorkspaceProxies } = useWorkspaceProxies();

  if (
    isLoadingAccountProxy ||
    (account.type === "user" && (isLoadingWorkspaceProxies || isLoadingProxies))
  ) {
    return <Skeleton className="inline-flex rounded-md h-5 w-16" />;
  }

  if (!accountProxy) {
    return "-";
  }

  let isValid = true;
  let location: { countryCode: string; name: string };
  if (accountProxy.length === 2) {
    const proxy = proxies.find(
      ({ countryCode }) => countryCode === accountProxy,
    );
    // if the selected proxy has been removed from our proxy list it's no longer valid
    if (proxy) {
      location = { countryCode: proxy.countryCode, name: proxy.country };
    } else {
      isValid = false;
    }
  } else {
    const proxy = workspaceProxies.find(({ id }) => id === accountProxy);

    if (proxy) {
      isValid = proxy.valid;

      if (isValid) {
        location = {
          countryCode: proxy.location.countryISO,
          name: proxy.location.city || proxy.location.country,
        };
      }
    } else {
      Sentry.captureMessage("Custom Proxy not found");
      isValid = false;
    }
  }

  return <ProxyLocationTag isValid={isValid} location={location} size="sm" />;
}
